/**
 * SanctaInputBar — the bottom form of the Sancta widget.
 *
 * Renders: detected-language chip · text input · voice-mic button (when
 * supported) · checkout toggle (auth only) · send/loading button.
 *
 * Extracted from SanctaChatbot.tsx. Fully controlled — parent owns input
 * value, streaming flag, checkout state, voice state, and submit handling.
 */
import React from "react";
import { Send, Loader2, Radio, Mic, MicOff, ShoppingBag, Volume2, VolumeX, ImagePlus } from "lucide-react";
import { SANCTA_LANGUAGES } from "@/hooks/useSanctaLocale";

type Detection = { code: string; confidence: number; source: string } | null;

interface VoiceHandle {
  supported: boolean;
  state: string;                  // "idle" | "listening" | "error" ...
}

interface Props {
  variant: "widget" | "page";
  input: string;
  setInput: (v: string) => void;
  streaming: boolean;
  onSubmit: (e: React.FormEvent) => void;
  placeholder: string;
  sendAriaLabel: string;
  detection: Detection;
  autoSwap: boolean;
  voice: VoiceHandle;
  onToggleVoice: () => void;
  showCheckoutBtn: boolean;         // true when authenticated
  checkoutOpen: boolean;
  onToggleCheckout: () => void;
  blocked?: boolean;                // Phase 2 — daily limit / read-only reached
  blockedMsg?: string;
  muted?: boolean;                  // Phase 3 — results-chime mute
  onToggleMute?: () => void;
  onPickImage?: (file: File) => void;   // Phase 4 — visual search
  imageBusy?: boolean;
}

export const SanctaInputBar: React.FC<Props> = ({
  variant, input, setInput, streaming, onSubmit, placeholder, sendAriaLabel,
  detection, autoSwap, voice, onToggleVoice, showCheckoutBtn, checkoutOpen,
  onToggleCheckout, blocked = false, blockedMsg, muted = false, onToggleMute,
  onPickImage, imageBusy = false,
}) => {
  const fileRef = React.useRef<HTMLInputElement | null>(null);
  const listening = voice.state === "listening";
  const disabled = streaming || blocked;
  return (
    <form
      onSubmit={onSubmit}
      className={`border-t p-2 flex items-center gap-2 bg-white transition-colors ${
        listening
          ? "border-rose-300 ring-2 ring-rose-500/15"
          : "border-neutral-200"
      }`}
    >
      {detection && detection.confidence >= 0.7 && (
        <span
          data-testid="sancta-detected-lang-chip"
          title={`Sancta auto-detected ${detection.code} (${detection.source}, ${Math.round(detection.confidence * 100)}% confident)`}
          className={`hidden sm:inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
            autoSwap
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
              : "bg-slate-50 text-slate-500 border border-slate-200"
          }`}
        >
          <Radio className="h-2.5 w-2.5" />
          {SANCTA_LANGUAGES.find(l => l.code === detection.code)?.nativeName || detection.code.toUpperCase()}
        </span>
      )}

      <input
        data-testid="sancta-input"
        value={input}
        onChange={e => setInput(e.target.value)}
        placeholder={blocked ? (blockedMsg || "Daily limit reached — resets at midnight IST") : listening ? "Listening… speak now." : placeholder}
        disabled={disabled}
        className={`flex-1 px-3 py-2 rounded-full text-sm bg-neutral-100 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:opacity-60 ${
          listening ? "bg-rose-50 placeholder:text-rose-400" : ""
        }`}
      />

      {onPickImage && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            data-testid="sancta-image-input"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onPickImage(f);
              if (fileRef.current) fileRef.current.value = "";
            }}
          />
          <button
            type="button"
            data-testid="sancta-image-attach"
            onClick={() => fileRef.current?.click()}
            disabled={disabled || imageBusy}
            aria-label="Search by product photo"
            title="Search by product photo"
            className="h-9 w-9 rounded-full grid place-items-center bg-neutral-100 text-neutral-500 hover:bg-fuchsia-100 hover:text-fuchsia-600 transition-colors disabled:opacity-40"
          >
            {imageBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          </button>
        </>
      )}

      {onToggleMute && (
        <button
          type="button"
          data-testid="sancta-mute-toggle"
          onClick={onToggleMute}
          aria-pressed={muted}
          aria-label={muted ? "Unmute Sancta sounds" : "Mute Sancta sounds"}
          title={muted ? "Sounds off — tap to unmute" : "Mute result chime"}
          className={`h-9 w-9 rounded-full grid place-items-center transition-colors ${
            muted ? "bg-neutral-100 text-neutral-400" : "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
          }`}
        >
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>
      )}

      {voice.supported && (
        <button
          type="button"
          data-testid="sancta-voice-toggle"
          onClick={onToggleVoice}
          aria-pressed={listening}
          aria-label={listening ? "Stop voice input" : "Start voice input"}
          title={listening
            ? "Tap to stop listening"
            : streaming
              ? "Wait for Sancta to finish, then tap and speak"
              : "Tap and speak your question"}
          disabled={disabled}
          className={`h-9 w-9 rounded-full grid place-items-center transition-colors disabled:opacity-40 ${
            listening
              ? "bg-rose-500 text-white shadow animate-pulse"
              : "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
          }`}
        >
          {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </button>
      )}

      {showCheckoutBtn && (
        <button
          type="button"
          data-testid="sancta-checkout-toggle"
          onClick={onToggleCheckout}
          aria-pressed={checkoutOpen}
          aria-label={checkoutOpen ? "Close checkout" : "Open Sancta checkout"}
          title={checkoutOpen ? "Close checkout" : "Checkout with Sancta"}
          className={`h-9 w-9 rounded-full grid place-items-center transition-colors ${
            checkoutOpen
              ? "bg-emerald-500 text-white"
              : "bg-neutral-100 text-neutral-600 hover:bg-emerald-100 hover:text-emerald-700"
          }`}
        >
          <ShoppingBag className="h-4 w-4" />
        </button>
      )}

      <button
        type="submit"
        data-testid="sancta-send"
        disabled={!input.trim() || disabled}
        className="h-9 w-9 rounded-full bg-gradient-to-br from-orange-500 to-fuchsia-600 text-white grid place-items-center disabled:opacity-40"
        aria-label={sendAriaLabel}
      >
        {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
      </button>
    </form>
  );
};

export default SanctaInputBar;
